import { BrowserRouter, Routes, NavLink, Route } from "react-router-dom";
import { useState, useEffect } from "react";
import {
  FaBars, FaEnvelope, FaMapMarkerAlt,
  FaTelegram, FaTimes, FaWhatsapp,
} from "react-icons/fa";

import Home from "./components/home";
import Blog from "./components/blog";
import Services from "./components/services";
import Contact from "./components/contact";
import Cryptoservices from "./components/cryptoservices";
import Preload from "./components/preload";
import FAQ from "./components/faq";
import { ScrollToTop } from "./scrollToTop";
import weigh from './assets/weigh-removeBg copy.png'

// ─── Data ────────────────────────────────────────────────────────────────────

const NAV_LINKS = [
  { to: "/", label: "Home" },
  { to: "#goalsect", label: "About", isAnchor: true },
  { to: "/services", label: "Services" },
  { to: "/blogs", label: "Blogs" },
  { to: "/faq", label: "FAQs" },
];

const FOOTER_QUICK_LINKS = [
  { to: "/", label: "Home" },
  { to: "/services", label: "Services" },
  { to: "/blogs", label: "Blogs" },
  { to: "/faq", label: "FAQs" },
];

const FOOTER_SERVICES = [
  "Crypto Services",
  "Hacking & Blackmail",
  "Wallet Recovery",
  "Asset Recovery",
  "Asset Consultation",
];

const FOOTER_SOCIALS = [
  { href: "https://wa.me/8562054097092", icon: <FaWhatsapp size={14} /> },
  { href: "https://t.me/shieldcrypt_help", icon: <FaTelegram size={14} /> },
  { href: "mailto:support@shieldcrypt.help", icon: <FaEnvelope size={14} /> },
];

// ─── Component ───────────────────────────────────────────────────────────────

const App = () => {
  const [showNav, setShowNav] = useState(false);
  const [loading, setLoading] = useState(true);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setLoading(false), 600);
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => { clearTimeout(timer); window.removeEventListener("scroll", onScroll); };
  }, []);

  return (
    <BrowserRouter>
      {/* Global font + tiny overrides that Tailwind can't express */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Serif+Display:ital@0;1&family=DM+Sans:opsz,wght@9..40,300;9..40,400;9..40,500;9..40,600&display=swap');
        html, body { font-family: 'DM Sans', sans-serif; background: #F7F3EC; color: #1C1A16; }
        .sc-serif   { font-family: 'DM Serif Display', serif; }
        .scrollbar-hide::-webkit-scrollbar { display: none; }
        .scrollbar-hide { -ms-overflow-style: none; scrollbar-width: none; }

        /* Phone-input override – library injects its own markup */
        .custom-phone-input .PhoneInputInput {
          background: #EDE7D9; border: 1px solid #D6CDB8; border-left: none;
          color: #1C1A16; padding: 0.55rem 0.75rem; font-size: 0.875rem;
          outline: none; font-family: 'DM Sans', sans-serif; height: 44px; width: 100%;
        }
        .custom-phone-input .PhoneInputCountry {
          background: #EDE7D9; border: 1px solid #D6CDB8;
          border-right: none; padding: 0 0.6rem;
        }
        .custom-phone-input .PhoneInputInput:focus { border-color: #C4622D; }
      `}</style>

      {loading ? <Preload /> : (
        <div className="min-h-screen bg-[#F7F3EC]">

          {/* ── Header ── */}
          <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? "bg-[#F7F3EC]/95 backdrop-blur-md border-b border-[#E0D9CC]" : "bg-[#F7F3EC]"
            }`}>
            <div className="max-w-[1100px] mx-auto px-8 h-16 flex items-center justify-between">

              {/* Logo */}
              <NavLink to="/" className="flex items-center gap-2 no-underline">
                <img src={weigh} alt='logo' className="w-10" />
                <div className="flex flex-col">
                  <p className="sc-serif text-[1.15rem] text-[#1C1A16] font-semibold -mb-2">ShieldCrypt</p>
                  <p className="text-sm italic text-gray-400">Supporting victims of cybercrime</p>
                </div>
              </NavLink>

              {/* Desktop nav */}
              <nav className="items-center hidden md:flex gap-9">
                {NAV_LINKS.map((link) =>
                  link.isAnchor ? (
                    <a
                      key={link.label}
                      href={link.to}
                      className="text-[#6B6457] text-sm font-medium no-underline hover:text-[#C4622D] transition-colors"
                    >
                      {link.label}
                    </a>
                  ) : (
                    <NavLink
                      key={link.label}
                      to={link.to}
                      className={({ isActive }) =>
                        `text-sm font-medium no-underline transition-colors ${isActive ? "text-[#C4622D]" : "text-[#6B6457] hover:text-[#C4622D]"
                        }`
                      }
                    >
                      {link.label}
                    </NavLink>
                  )
                )}
                <NavLink
                  to="/contact"
                  className="px-5 py-2 bg-[#1C1A16] text-[#F7F3EC] text-sm font-semibold no-underline hover:bg-[#C4622D] transition-colors"
                >
                  Contact
                </NavLink>
              </nav>

              {/* Mobile hamburger */}
              <button
                onClick={() => setShowNav((p) => !p)}
                className="md:hidden bg-transparent border-none cursor-pointer text-[#1C1A16]"
              >
                {showNav ? <FaTimes size={20} /> : <FaBars size={20} />}
              </button>
            </div>

            {/* Mobile nav drawer */}
            {showNav && (
              <div className="md:hidden bg-[#EDE7D9] border-t border-[#D6CDB8] px-8 py-6 flex flex-col gap-4">
                {NAV_LINKS.map((link) =>
                  link.isAnchor ? (
                    <a
                      key={link.label}
                      href={link.to}
                      onClick={() => setShowNav(false)}
                      className="text-[#1C1A16] text-[0.95rem] font-medium no-underline"
                    >
                      {link.label}
                    </a>
                  ) : (
                    <NavLink
                      key={link.label}
                      to={link.to}
                      onClick={() => setShowNav(false)}
                      className="text-[#1C1A16] text-[0.95rem] font-medium no-underline"
                    >
                      {link.label}
                    </NavLink>
                  )
                )}
                <NavLink
                  to="/contact"
                  onClick={() => setShowNav(false)}
                  className="self-start px-5 py-2 bg-[#1C1A16] text-[#F7F3EC] text-sm font-semibold no-underline"
                >
                  Contact
                </NavLink>
              </div>
            )}
          </header>

          {/* ── Page content ── */}
          <div className="pt-16">
            <ScrollToTop />
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/crypto-services" element={<Cryptoservices />} />
              <Route path="/blogs" element={<Blog />} />
              <Route path="/services" element={<Services />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/faq" element={<FAQ />} />
            </Routes>

            {/* ── Footer ── */}
            <footer className="bg-[#1C1A16] pt-20 pb-8 px-8" id="about">
              <div className="max-w-[1100px] mx-auto">

                <div className="grid grid-cols-1 gap-12 mb-16 sm:grid-cols-2 lg:grid-cols-4">

                  {/* Brand */}
                  <div>
                    <NavLink to="/" className="flex items-center gap-2 mb-3 no-underline">
                      <img src={weigh} alt='logo' className="w-10" />
                      <span className="sc-serif text-[1.1rem] text-[#F7F3EC]">ShieldCrypt</span>
                    </NavLink>
                    <p className="text-[#5C5A54] text-[0.82rem] italic leading-relaxed mb-6">
                      "The one who owns the information, owns the world!"
                    </p>
                    <div className="flex gap-2">
                      {FOOTER_SOCIALS.map((s, i) => (
                        <a
                          key={i}
                          href={s.href}
                          className="w-8 h-8 bg-[#2C2A24] flex items-center justify-center text-[#6B6457] no-underline hover:bg-[#C4622D] hover:text-white transition-all"
                        >
                          {s.icon}
                        </a>
                      ))}
                    </div>
                  </div>

                  {/* Quick links */}
                  <div>
                    <h3 className="text-[#F7F3EC] text-[0.68rem] font-semibold tracking-[0.14em] uppercase mb-5">
                      Quick Links
                    </h3>
                    <ul className="flex flex-col gap-3 p-0 m-0 list-none">
                      {FOOTER_QUICK_LINKS.map((item) => (
                        <li key={item.label}>
                          <NavLink
                            to={item.to}
                            className="text-[#5C5A54] text-[0.85rem] no-underline hover:text-[#C4622D] transition-colors"
                          >
                            {item.label}
                          </NavLink>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Services */}
                  <div>
                    <h3 className="text-[#F7F3EC] text-[0.68rem] font-semibold tracking-[0.14em] uppercase mb-5">
                      Services
                    </h3>
                    <ul className="flex flex-col gap-3 p-0 m-0 list-none">
                      {FOOTER_SERVICES.map((s) => (
                        <li key={s} className="text-[#5C5A54] text-[0.85rem]">{s}</li>
                      ))}
                    </ul>
                  </div>

                  {/* Contact */}
                  <div>
                    <h3 className="text-[#F7F3EC] text-[0.68rem] font-semibold tracking-[0.14em] uppercase mb-5">
                      Get in Touch
                    </h3>
                    <div className="flex flex-col gap-4">
                      <div className="flex items-start gap-2">
                        <FaMapMarkerAlt size={11} color="#C4622D" className="mt-[3px] shrink-0" />
                        <p className="text-[#5C5A54] text-[0.82rem] leading-relaxed">
                          Sytinskiy Lane, 5 с2, Moscow Russia 123104
                        </p>
                      </div>
                      <a
                        href="mailto:support@shieldcrypt.help"
                        className="flex items-center gap-2 no-underline group"
                      >
                        <FaEnvelope size={11} color="#C4622D" />
                        <span className="text-[#5C5A54] text-[0.82rem] group-hover:text-[#C4622D] transition-colors">
                          support@shieldcrypt.help
                        </span>
                      </a>
                      <a
                        href="https://t.me/shieldcrypt_help"
                        className="self-start mt-1 px-4 py-2 bg-[#C4622D] text-white text-[0.8rem] font-semibold no-underline hover:bg-[#a84f24] transition-colors"
                      >
                        Contact
                      </a>
                    </div>
                  </div>
                </div>

                <div className="border-t border-[#2C2A24] pt-6">
                  <p className="text-[#3C3A34] text-[0.75rem]">
                    ShieldCrypt © Copyright {new Date().getFullYear()}. All rights reserved
                  </p>
                </div>
              </div>
            </footer>
          </div>
        </div>
      )}
    </BrowserRouter>
  );
};

export default App;
